import React, { useEffect, useState } from 'react';
import { FileText, Calendar, MapPin, Copy, QrCode, ChevronRight, Ticket, History, XCircle, Headphones, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const loadAuth = () => {
    try {
      const userStr = localStorage.getItem('user_data');
      const t = localStorage.getItem('auth_token');
      if (userStr && t) {
        setUser(JSON.parse(userStr));
        setToken(t);
      } else {
        setUser(null);
        setToken(null);
        setLoading(false);
      }
    } catch (e) {
      console.error('Error parsing user:', e);
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadAuth();
    window.addEventListener('auth_changed', loadAuth);
    return () => window.removeEventListener('auth_changed', loadAuth);
  }, []);

  useEffect(() => {
    if (user && token) {
      setLoading(true);
      fetchTickets();
    }
  }, [user, token]);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`/api/fetch_tickets/${user.id}`, {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success && data.tickets) {
        setTickets(data.tickets);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen pb-36 md:pb-20 font-sans animate-pulse">
        {/* Header Skeleton */}
        <div className="bg-[#0c0515] pt-12 pb-24 px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="h-10 bg-gray-800 w-48 rounded mb-2"></div>
            <div className="h-4 bg-gray-800 w-64 rounded"></div>
          </div>
        </div>

        {/* Main Content Area Skeleton */}
        <div className="max-w-3xl mx-auto -mt-10 relative z-10 px-0 sm:px-4">
          <div className="bg-white rounded-t-[30px] px-4 md:px-8 pt-6 flex overflow-x-auto gap-6 border-b border-gray-100">
            <div className="h-6 bg-gray-200 w-24 rounded-full mb-4"></div>
            <div className="h-6 bg-gray-200 w-24 rounded-full mb-4"></div>
          </div>
          
          <div className="p-4 md:p-0 md:mt-6 space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-[24px] shadow-[0_2px_10px_rgba(0,0,0,0.03)] border border-gray-100 p-4 md:p-5 h-[160px] flex gap-4">
                 <div className="w-[100px] md:w-[130px] h-[120px] bg-gray-200 rounded-xl shrink-0"></div>
                 <div className="flex-1 flex flex-col gap-2">
                    <div className="h-5 bg-gray-200 w-3/4 rounded"></div>
                    <div className="h-4 bg-gray-200 w-1/2 rounded"></div>
                    <div className="h-4 bg-gray-200 w-full rounded mt-auto"></div>
                 </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user || !token) {
    return (
      <div className="min-h-[70vh] bg-[#f8fafc] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm text-center max-w-md w-full border border-gray-100">
          <div className="text-gray-300 flex justify-center mb-4">
            <FileText size={64} />
          </div>
          <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-2">Sign In Required</h2>
          <p className="text-gray-500 mb-6">Please log in to view your booked tickets.</p>
          <button 
             onClick={() => window.dispatchEvent(new Event('openLoginModal'))}
             className="w-full bg-[#d33a33] text-white font-bold py-3 rounded-xl hover:bg-red-700 transition"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const upcomingTickets = tickets.filter(t => {
    if (!t.product?.jatra_date) return true;
    const date = new Date(t.product.jatra_date);
    return date >= now;
  });

  const pastTickets = tickets.filter(t => {
    if (!t.product?.jatra_date) return false;
    const date = new Date(t.product.jatra_date);
    return date < now;
  });

  const displayTickets = activeTab === 'upcoming' ? upcomingTickets : pastTickets;

  return (
    <div className="bg-gray-50 min-h-screen pb-36 md:pb-20 font-sans" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Header */}
      <div className="bg-[#0c0515] pt-12 pb-24 px-4 md:px-8">
        <div className="max-w-3xl mx-auto flex justify-between items-start">
          <div>
            <h1 className="text-xl md:text-4xl font-extrabold text-white">My Tickets</h1>
            <p className="text-gray-400 mt-2 text-sm md:text-base">Manage and view your booked<br className="hidden md:block"/> performance tickets.</p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-3xl mx-auto -mt-10 relative z-10 px-0 sm:px-4">
        <div className="bg-gray-50 sm:bg-transparent rounded-t-[30px] sm:rounded-none overflow-hidden">
          
          {/* Tabs */}
          <div className="bg-white rounded-t-[30px] px-4 md:px-8 pt-6 flex overflow-x-auto gap-6 hide-scrollbar border-b border-gray-100">
            <button 
              onClick={() => setActiveTab('upcoming')}
              className={`flex items-center gap-2 font-bold pb-4 whitespace-nowrap shrink-0 text-sm transition ${activeTab === 'upcoming' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-400 hover:text-gray-600'}`}>
              <Ticket size={16} /> Upcoming ({upcomingTickets.length})
            </button>
            <button 
              onClick={() => setActiveTab('past')}
              className={`flex items-center gap-2 font-bold pb-4 whitespace-nowrap shrink-0 text-sm transition ${activeTab === 'past' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-400 hover:text-gray-600'}`}>
              <History size={16} /> Past ({pastTickets.length})
            </button>
          </div>

          <div className="p-4 md:p-0 md:mt-6 space-y-4">
            {error && (
              <div className="bg-red-50 text-red-500 p-4 rounded-xl mb-4 font-bold text-center border border-red-100">
                {error}
              </div>
            )}

            {displayTickets.length === 0 && !error ? (
              <div className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center">
                <div className="bg-gray-50 p-6 rounded-full mb-6">
                  <FileText size={48} className="text-gray-300" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">No Tickets Found</h2>
                <p className="text-gray-500 max-w-md mb-8">You haven't booked any {activeTab} shows.</p>
                <button 
                  onClick={() => navigate('/shows')}
                  className="bg-red-600 text-white font-bold py-3 px-8 rounded-xl hover:bg-red-700 transition"
                >
                  Browse Shows
                </button>
              </div>
            ) : (
              displayTickets.map((ticket, idx) => (
                <div key={idx} className="bg-white rounded-[24px] shadow-[0_2px_10px_rgba(0,0,0,0.03)] border border-gray-100 overflow-hidden p-4 md:p-5">
                  <div className="flex gap-4">
                    {/* Poster */}
                    <div className="w-24 h-[132px] md:w-[120px] md:h-[160px] shrink-0 bg-gray-100 rounded-xl overflow-hidden relative shadow-inner">
                      <img 
                        src={ticket.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} 
                        alt={ticket.jatra_name || 'Jatra Show'}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
                      />
                    </div>
                    {/* Details */}
                    <div className="flex-1 min-w-0 py-1">
                      <div className="flex justify-between items-start mb-2">
                        <div className="inline-block px-2.5 py-1 bg-green-50 text-green-600 text-[10px] font-black uppercase tracking-widest rounded-md border border-green-100/50">
                          CONFIRMED
                        </div>
                        <div className="text-right hidden md:block">
                          <p className="text-[9px] text-gray-400 mb-0.5 uppercase tracking-wider font-bold">Order ID</p>
                          <div className="flex items-center justify-end gap-1 max-w-[100px] sm:max-w-[120px]">
                            <p className="text-xs font-bold text-gray-500 truncate">#{ticket.order_id || ticket.booking_id}</p>
                            <Copy size={12} className="shrink-0 text-gray-400 cursor-pointer hover:text-gray-700" />
                          </div>
                        </div>
                      </div>
                      <h3 className="text-base md:text-lg font-black text-gray-900 leading-tight mb-3 pr-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {ticket.jatra_name || 'Jatra Show'}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600 text-xs font-medium">
                          <Calendar size={14} className="mr-2 shrink-0 text-gray-400" />
                          <span>{ticket.jatra_date ? new Date(ticket.jatra_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'} &bull; {ticket.jatra_time || '11:15 PM'}</span>
                        </div>
                        <div className="flex items-start text-gray-600 text-xs font-medium">
                          <MapPin size={14} className="mr-2 shrink-0 mt-0.5 text-gray-400" />
                          <div>
                            <p>{ticket.jatra_location || 'Venue details'}</p>
                            <p className="text-[10px] mt-0.5 text-gray-400 font-normal">Bhubaneswar, Odisha</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gray Seats Box */}
                  <div className="bg-[#f9fafc] rounded-2xl p-3 md:p-4 mt-4 flex items-center divide-x divide-gray-200/60 border border-gray-100">
                    <div className="flex-1 text-center">
                      <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Seats Booked</p>
                      <p className="font-black text-gray-800 text-sm md:text-base">{ticket.total_seat_name || 'N/A'}</p>
                    </div>
                    <div className="flex-1 text-center">
                      <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Total Paid</p>
                      <p className="font-black text-red-600 text-sm md:text-base">₹{Number(ticket.total_amount || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Entry Pass or Scanned Tag */}
                  {ticket.scan_status === 1 ? (
                    <div className="border-t border-dashed border-gray-200 mt-5 pt-4 flex items-center justify-between px-2 pb-1">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center border border-green-200 shrink-0">
                          <Ticket size={20} className="text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-gray-800 mb-0.5">Entry Pass</p>
                          <div className="inline-flex items-center gap-1 text-[11px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-md">
                            Successfully Scanned
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-dashed border-gray-200 mt-5 pt-4 flex items-center justify-between cursor-pointer group px-2 pb-1" onClick={() => setSelectedTicket(ticket)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200 shrink-0">
                          <QrCode size={20} className="text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-gray-800 mb-0.5">Entry Pass</p>
                          <p className="text-[11px] text-gray-500 font-medium">Tap to view QR code</p>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-gray-400 group-hover:text-gray-900 transition" />
                    </div>
                  )}
                </div>
              ))
            )}
            
            {/* Contact Support */}
            {displayTickets.length > 0 && (
              <div className="bg-[#fff9f9] border border-red-100 rounded-[24px] p-5 md:p-6 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 mt-6">
                <div className="w-12 h-12 bg-red-100/50 rounded-full flex items-center justify-center shrink-0">
                  <Headphones size={22} className="text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="font-black text-gray-900 text-base mb-1">Need Help?</p>
                  <p className="text-xs text-gray-500 leading-relaxed font-medium">Contact our support team for any ticket related issues.</p>
                </div>
                <button className="bg-white border border-red-200 text-red-600 text-xs font-black px-5 py-3 rounded-xl shrink-0 hover:bg-red-50 transition shadow-sm mt-2 sm:mt-0">
                  Contact Support
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
          <div className="bg-white w-full max-w-sm rounded-[24px] shadow-2xl overflow-hidden transform transition-all scale-100">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold text-lg">Entry Pass</h3>
              <button 
                onClick={() => setSelectedTicket(null)}
                className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-8 flex flex-col items-center justify-center">
              <div className="bg-white p-3 rounded-2xl shadow-[0_0_15px_rgba(0,0,0,0.1)] mb-4 border border-gray-100">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${selectedTicket.booking_id}`}
                  alt="Ticket QR Code" 
                  className="w-48 h-48 object-contain"
                />
              </div>
              <p className="text-gray-500 text-sm font-medium mb-1">Order ID</p>
              <p className="font-black text-xl text-gray-900 tracking-wider">#{selectedTicket.order_id || selectedTicket.booking_id}</p>
              <p className="text-xs text-red-500 font-bold mt-4 uppercase tracking-widest bg-red-50 px-3 py-1 rounded-full">Scan at Entrance</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
